/**
 * Durable qualified-listening history keyed by canonical recordingId.
 *
 * Path: users/{uid}/listeningStats/{recordingId}
 * Occurrence dedupe: users/{uid}/listeningStats/{recordingId}/occurrences/{occurrenceId}
 *
 * This is factual play-count memory — not inferred taste / affinity.
 *
 * Authority note (Favorites v1): writes are owner-scoped. A malicious user can
 * inflate their own counts; that is acceptable while these numbers are only
 * used for personal Favorites ranking and are NOT trusted cross-user
 * recommendation evidence. Honest clients still idempotent-count via the
 * occurrences subcollection + in-memory occurrence guard.
 */

import {
  doc,
  getDoc,
  runTransaction,
  collection,
  getDocs,
  limit,
  query,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { describeError } from '../utils/errorLog';

export const LISTENING_STATS_COLLECTION = 'listeningStats';
export const QUALIFIED_LISTEN_MIN_SECONDS = 120;
export const QUALIFIED_LISTEN_FRACTION = 0.5;

export function listeningStatsRef(uid, recordingId) {
  return doc(db, 'users', uid, LISTENING_STATS_COLLECTION, recordingId);
}

export function listeningOccurrenceRef(uid, recordingId, occurrenceId) {
  return doc(
    db,
    'users',
    uid,
    LISTENING_STATS_COLLECTION,
    recordingId,
    'occurrences',
    occurrenceId,
  );
}

export function isCanonicalRecordingId(value) {
  return typeof value === 'string' && value.startsWith('rhy_rec_');
}

/**
 * Qualified when listened to min(50% of duration, 120s).
 * If duration is unknown, fall back to the 120s floor only.
 */
export function hasQualifiedListenProgress({
  currentTimeSeconds = 0,
  durationSeconds = 0,
} = {}) {
  const heard = Math.max(0, Number(currentTimeSeconds) || 0);
  const duration = Math.max(0, Number(durationSeconds) || 0);
  if (heard <= 0) return false;
  if (duration > 0) {
    const threshold = Math.min(
      QUALIFIED_LISTEN_MIN_SECONDS,
      duration * QUALIFIED_LISTEN_FRACTION,
    );
    return heard >= threshold;
  }
  return heard >= QUALIFIED_LISTEN_MIN_SECONDS;
}

export function buildOccurrenceId({ queueItemId, recordingId, startedAt } = {}) {
  if (typeof queueItemId === 'string' && queueItemId.trim()) {
    return queueItemId.trim().replace(/[/#[\]]/g, '_').slice(0, 700);
  }
  const started = Number(startedAt) || 0;
  return `${recordingId || 'unknown'}:${started}`;
}

export function emptyListeningStat(recordingId, now = Date.now()) {
  return {
    recordingId,
    totalQualifiedPlays: 0,
    soloQualifiedPlays: 0,
    groupQualifiedPlays: 0,
    lastQualifiedPlayedAt: null,
    createdAt: now,
    updatedAt: now,
    title: null,
    artist: null,
  };
}

/**
 * Count one qualified listen for an occurrence. Idempotent per occurrenceId.
 */
export async function recordQualifiedListen({
  uid,
  recordingId,
  occurrenceId,
  mode = 'solo',
  title = null,
  artist = null,
  now = Date.now(),
} = {}) {
  if (!uid || !isCanonicalRecordingId(recordingId) || !occurrenceId) {
    return { ok: false, reason: 'invalid' };
  }
  const statsRef = listeningStatsRef(uid, recordingId);
  const occurrenceRef = listeningOccurrenceRef(uid, recordingId, occurrenceId);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const occurrenceSnap = await transaction.get(occurrenceRef);
      if (occurrenceSnap.exists()) {
        return { ok: true, counted: false, reason: 'already-counted' };
      }
      const statsSnap = await transaction.get(statsRef);
      const existing = statsSnap.exists()
        ? statsSnap.data()
        : emptyListeningStat(recordingId, now);
      const soloDelta = mode === 'group' ? 0 : 1;
      const groupDelta = mode === 'group' ? 1 : 0;
      const next = {
        recordingId,
        totalQualifiedPlays: (Number(existing.totalQualifiedPlays) || 0) + 1,
        soloQualifiedPlays: (Number(existing.soloQualifiedPlays) || 0) + soloDelta,
        groupQualifiedPlays: (Number(existing.groupQualifiedPlays) || 0) + groupDelta,
        lastQualifiedPlayedAt: now,
        createdAt: existing.createdAt || now,
        updatedAt: now,
        title: typeof title === 'string' && title.trim()
          ? title.trim().slice(0, 200)
          : (existing.title || null),
        artist: typeof artist === 'string' && artist.trim()
          ? artist.trim().slice(0, 200)
          : (existing.artist || null),
      };
      transaction.set(statsRef, next);
      transaction.set(occurrenceRef, {
        occurrenceId,
        countedAt: now,
        mode: mode === 'group' ? 'group' : 'solo',
      });
      return { ok: true, counted: true, stat: next };
    });
    return result;
  } catch (error) {
    console.error('recordQualifiedListen failed:', describeError(error));
    return { ok: false, reason: 'write-failed' };
  }
}

export async function fetchListeningStats(uid, { max = 100 } = {}) {
  if (!uid) return [];
  try {
    const snap = await getDocs(query(
      collection(db, 'users', uid, LISTENING_STATS_COLLECTION),
      orderBy('totalQualifiedPlays', 'desc'),
      limit(max),
    ));
    return snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
  } catch (error) {
    console.error('fetchListeningStats failed:', describeError(error));
    return [];
  }
}

export async function fetchListeningStat(uid, recordingId) {
  if (!uid || !isCanonicalRecordingId(recordingId)) return null;
  try {
    const snap = await getDoc(listeningStatsRef(uid, recordingId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error('fetchListeningStat failed:', describeError(error));
    return null;
  }
}
